import React, { useCallback, useState, useMemo } from "react";
import PropTypes from "prop-types";
import { useHistory } from "react-router-dom";
import { useDispatch } from "react-redux";
import { reduxForm } from "redux-form";
import { createContentFeed } from "reducers/actions/ContentFeedActions";
import { buildCreateContentFeedPayload } from "utils/ContentFeedCreation";
import { FEED_CREATION_FORM } from "forms/list";
import BaseContentFeedForm from "widgets/forms/contentfeeds/BaseContentFeedForm";
import ContentFeedMappingsGrid from "routes/contentfeeds/ContentFeedMappingsGrid";
import FeedCreationStepper from "routes/contentfeeds/FeedCreationStepper";
import { createContentFeedNavigation } from "utils/ContentFeedFormUtils";

const STEP_NAMES = ["General Settings", "Configure Mappings"];
const VALIDATION_FIELDS = ["feedName", "sourceUrl", "sourceType", "sheetName", "feedEmails"];

function CreateContentFeedForm({ libraryId, submitting, handleSubmit }) {
	const history = useHistory();
	const dispatch = useDispatch();
	const [finishedFirstStep, setFinishedFirstStep] = useState(false);

	const { goBackToFeedsList, dispatchWithFeedsListRedirect } = useMemo(
		() => createContentFeedNavigation(history, libraryId),
		[history, libraryId]
	);

	const handleCreateSave = useCallback((formValues) => {
		const payload = buildCreateContentFeedPayload({ formValues, libraryId });
		return dispatchWithFeedsListRedirect(dispatch(createContentFeed(payload)));
	}, [libraryId, dispatchWithFeedsListRedirect]);

	const handleFormSubmit = useCallback((formValues) => {
		setFinishedFirstStep(true);
	}, []);

	const handleMappingsSave = useCallback(
		event => handleSubmit(handleCreateSave)(event),
		[handleCreateSave, handleSubmit]
	);

	const handleBack = useCallback(() => {
		setFinishedFirstStep(false);
	}, []);

	if (finishedFirstStep) {
		return (
			<>
				<FeedCreationStepper stepNames={STEP_NAMES} currentStep={1} />
				<ContentFeedMappingsGrid
					libraryId={libraryId}
					onSave={handleMappingsSave}
					onCancel={goBackToFeedsList}
					onBack={handleBack}
					submitting={submitting}
				/>
			</>
		);
	}

	const formProps = {
		onSubmit: handleFormSubmit,
		onCancel: goBackToFeedsList,
		submitButtonTitle: "Next: Configure Mappings",
		reduxFormHandleSubmit: handleSubmit,
		isSaveLoading: false,
		validationFields: VALIDATION_FIELDS,
		showSourceFields: true,
		showSettingsFields: true,
		isAssignBlankFlow: false,
	};

	return (
		<>
			<FeedCreationStepper stepNames={STEP_NAMES} currentStep={0} />
			<BaseContentFeedForm {...formProps} />
		</>
	);
}

CreateContentFeedForm.propTypes = {
	libraryId: PropTypes.string.isRequired,
	submitting: PropTypes.bool,
	handleSubmit: PropTypes.func.isRequired,
};

export default reduxForm({
	form: FEED_CREATION_FORM,
	initialValues: {
		autoUpdates: true,
		autoMapping: true,
	},
})(CreateContentFeedForm);
